/* lazy-video.js
   Mounts deferred <source data-src="..."> URLs onto <video> elements rendered
   by snippets/lazy-video.liquid. Two modes:

   1. data-evr-bg-defer
      Sources attach after window.load + requestIdleCallback. Used for
      above-the-fold backgrounds: the poster carries first paint, the video
      kicks in once the LCP/FCP critical path is clear.

   2. data-evr-bg-lazy
      Sources attach via IntersectionObserver when scrolled near (200px margin).
      If the element also has data-evr-bg-autoplay="true", the video plays when
      visible and pauses when it leaves. Without that flag, sources mount but
      playback is left to existing UI controls (e.g. testimonial card buttons).

   Idempotent — safe to load multiple times.
*/
(() => {
  if (window.__evrLazyVideoInit) return;
  window.__evrLazyVideoInit = true;

  function attachSources(v) {
    if (!v || v.dataset.evrAttached) return false;

    let attached = false;
    v.querySelectorAll("source[data-src]").forEach((s) => {
      s.setAttribute("src", s.dataset.src);
      s.removeAttribute("data-src");
      attached = true;
    });

    if (v.dataset.srcMp4) {
      v.setAttribute("src", v.dataset.srcMp4);
      delete v.dataset.srcMp4;
      attached = true;
    }

    if (attached) {
      v.dataset.evrAttached = "1";
      try { v.load(); } catch (_) {}
    }
    return attached;
  }

  // Public helper for sections (e.g. testimonials) that need to force-attach
  // sources synchronously before calling .play() — handles the edge case where
  // a user clicks play before the IntersectionObserver callback has fired.
  window.evrLazyVideo = { attach: attachSources };

  function safePlay(v) {
    const p = v.play();
    if (p && typeof p.catch === "function") p.catch(() => {});
  }

  // Above-the-fold: defer until after first paint / page load is settled.
  function setupDeferred() {
    const vids = Array.from(document.querySelectorAll("video[data-evr-bg-defer]"));
    if (!vids.length) return;

    const run = () => {
      vids.forEach((v) => {
        attachSources(v);
        safePlay(v);
      });
    };

    const schedule = () => {
      if ("requestIdleCallback" in window) {
        window.requestIdleCallback(run, { timeout: 1500 });
      } else {
        setTimeout(run, 100);
      }
    };

    if (document.readyState === "complete") {
      schedule();
    } else {
      window.addEventListener("load", schedule, { once: true });
    }
  }

  // Below-the-fold: attach + play when scrolled into view; pause when out.
  function setupLazy() {
    const vids = Array.from(document.querySelectorAll("video[data-evr-bg-lazy]"));
    if (!vids.length) return;

    if (!("IntersectionObserver" in window)) {
      vids.forEach((v) => {
        attachSources(v);
        if (v.dataset.evrBgAutoplay === "true") safePlay(v);
      });
      return;
    }

    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const v = entry.target;
          if (entry.isIntersecting) {
            attachSources(v);
            if (v.dataset.evrBgAutoplay === "true") safePlay(v);
          } else if (v.dataset.evrBgAutoplay === "true") {
            try { v.pause(); } catch (_) {}
          }
        });
      },
      { rootMargin: "200px 0px", threshold: 0.1 }
    );

    vids.forEach((v) => io.observe(v));
  }

  function init() {
    setupDeferred();
    setupLazy();
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init, { once: true });
  } else {
    init();
  }
})();
